import React, { useState, useEffect, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { CenecLoadingBlock, CenecLoadingRow } from './CenecSpinner';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const VARIABLE_INFO = {
  ghi: { label: 'GHI (W/m\u00B2)', unit: 'W/m\u00B2', colorscale: 'YlOrRd', color: '#F59E0B', positive: true },
  dni: { label: 'DNI (W/m\u00B2)', unit: 'W/m\u00B2', colorscale: 'YlOrRd', color: '#EF4444', positive: true },
  dhi: { label: 'DHI (W/m\u00B2)', unit: 'W/m\u00B2', colorscale: 'YlOrBr', color: '#F97316', positive: true },
  temperature: { label: 'Temperature (\u00B0C)', unit: '\u00B0C', colorscale: 'RdBu_r', color: '#EF4444', positive: false },
  wind_speed: { label: 'Wind Speed (m/s)', unit: 'm/s', colorscale: 'Blues', color: '#3B82F6', positive: true },
  humidity: { label: 'Relative Humidity (%)', unit: '%', colorscale: 'Blues', color: '#06B6D4', positive: true },
  pressure: { label: 'Surface Pressure (hPa)', unit: 'hPa', colorscale: 'Viridis', color: '#8B5CF6', positive: false },
  precipitation: { label: 'Precipitation (mm)', unit: 'mm', colorscale: 'Blues', color: '#14B8A6', positive: true },
};

const darkLayout = {
  paper_bgcolor: 'rgba(0,0,0,0)',
  plot_bgcolor: 'rgba(30,42,74,0.3)',
  font: { color: '#f0f4f3', family: 'Work Sans, sans-serif', size: 11 },
  margin: { t: 40, r: 20, b: 40, l: 50 },
  xaxis: { gridcolor: 'rgba(78,204,163,0.1)' },
  yaxis: { gridcolor: 'rgba(78,204,163,0.1)' },
};

const plotConfig = { responsive: true, displayModeBar: false };

/**
 * Open a Plotly chart in a new browser tab at full screen.
 * Serializes data + layout, injects into a standalone HTML page with Plotly CDN.
 */
function openChartFullscreen(data, layout, title) {
  // Deep clone and adjust layout for fullscreen
  const fsLayout = JSON.parse(JSON.stringify(layout));
  fsLayout.paper_bgcolor = '#1a1a2e';
  fsLayout.plot_bgcolor = 'rgba(30,42,74,0.5)';
  fsLayout.height = undefined; // let it fill the viewport
  fsLayout.autosize = true;
  if (fsLayout.margin) {
    fsLayout.margin = { t: 60, r: 60, b: 60, l: 80 };
  }
  if (fsLayout.title && typeof fsLayout.title === 'object') {
    fsLayout.title.font = { ...fsLayout.title.font, size: 18 };
  }

  const html = `<!DOCTYPE html>
<html><head>
<title>${title || 'NRG Chart'}</title>
<script src="https://cdn.plot.ly/plotly-2.35.0.min.js"></script>
<style>
  body { margin:0; background:#1a1a2e; overflow:hidden; }
  #chart { width:100vw; height:100vh; }
</style>
</head><body>
<div id="chart"></div>
<script>
  var data = ${JSON.stringify(data)};
  var layout = ${JSON.stringify(fsLayout)};
  Plotly.newPlot('chart', data, layout, {responsive:true, displayModeBar:true});
  window.addEventListener('resize', function() { Plotly.Plots.resize('chart'); });
</script>
</body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Clean up after a delay
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Chart wrapper — adds expand-to-new-tab button to any Plot */
const ChartBox = ({ data, layout, config, title, style, children }) => (
  <div className="chart-container">
    <button
      className="chart-expand-btn"
      title="Open in full screen"
      onClick={() => openChartFullscreen(data, layout, title)}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
        <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
      </svg>
    </button>
    <Plot data={data} layout={layout} config={config || plotConfig} style={style || { width: '100%' }} />
  </div>
);

/* =========================================================================
   Rangeslider x-axis layout — used by overview and timeseries charts.
   Plotly's built-in rangeslider gives drag handles on both ends,
   click-and-drag the middle to pan, and rangeselector buttons for presets.
   ========================================================================= */
function makeRangeXaxis(dataStart, dataEnd) {
  // Default view: full year
  return {
    type: 'date',
    gridcolor: 'rgba(78,204,163,0.1)',
    rangeslider: {
      visible: true,
      bgcolor: 'rgba(30,42,74,0.5)',
      bordercolor: 'rgba(78,204,163,0.2)',
      borderwidth: 1,
      thickness: 0.08, // height of slider as fraction of plot
    },
    rangeselector: {
      bgcolor: 'rgba(30,42,74,0.8)',
      activecolor: 'rgba(78,204,163,0.3)',
      bordercolor: 'rgba(78,204,163,0.2)',
      borderwidth: 1,
      font: { color: '#f0f4f3', size: 10 },
      x: 0, y: 1.06,
      buttons: [
        { count: 1, label: 'Day', step: 'day', stepmode: 'backward' },
        { count: 7, label: 'Week', step: 'day', stepmode: 'backward' },
        { count: 1, label: 'Month', step: 'month', stepmode: 'backward' },
        { count: 3, label: '3 Mon', step: 'month', stepmode: 'backward' },
        { count: 6, label: '6 Mon', step: 'month', stepmode: 'backward' },
        { step: 'all', label: 'Year' },
      ],
    },
  };
}


const ClimateCharts = ({ datasetId }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapVar, setHeatmapVar] = useState('ghi');
  const [dailyData, setDailyData] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [hourlyMeta, setHourlyMeta] = useState(null);
  const [durationCurves, setDurationCurves] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/datasets/${datasetId}/stats`);
      const json = await res.json();
      setStats(json.stats);
    } catch (e) { console.error('Failed to fetch stats:', e); }
  }, [datasetId]);

  const fetchHeatmap = useCallback(async (variable) => {
    try {
      const res = await fetch(`${API}/api/datasets/${datasetId}/heatmap?variable=${variable}`);
      const json = await res.json();
      setHeatmapData(json);
    } catch (e) { console.error('Failed to fetch heatmap:', e); }
  }, [datasetId]);

  const fetchDaily = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/datasets/${datasetId}/data?agg=daily`);
      const json = await res.json();
      setDailyData(json.data);
    } catch (e) { console.error('Failed to fetch daily:', e); }
  }, [datasetId]);

  const fetchHourly = useCallback(async () => {
    try {
      // Adaptive downsample on the server (TimescaleDB time_bucket).
      // 2000 points is plenty for a chart at typical viewport widths;
      // payload drops ~500x vs raw hourly, no client-side sort over 100K rows.
      const points = 2000;
      const res = await fetch(`${API}/api/datasets/${datasetId}/data?agg=auto&points=${points}`);
      const json = await res.json();
      setHourlyData(json.data);
      setHourlyMeta({ count: json.count, bucket_hours: json.bucket_hours, agg: json.agg });
    } catch (e) { console.error('Failed to fetch hourly:', e); }
  }, [datasetId]);

  const fetchDurationCurve = useCallback(async (variable) => {
    try {
      // Server-computed via timescaledb_toolkit tdigest — runs against the full
      // hypertable in milliseconds regardless of dataset length.
      const res = await fetch(`${API}/api/datasets/${datasetId}/duration-curve?variable=${variable}`);
      const json = await res.json();
      setDurationCurves(prev => ({ ...prev, [variable]: json }));
    } catch (e) { console.error('Failed to fetch duration curve:', e); }
  }, [datasetId]);

  useEffect(() => {
    if (!datasetId) return;
    setLoading(true);
    setStats(null);
    setDailyData(null);
    setHourlyData(null);
    setHourlyMeta(null);
    setDurationCurves({});
    Promise.all([fetchStats(), fetchHeatmap(heatmapVar), fetchDaily()])
      .finally(() => setLoading(false));
  }, [datasetId, fetchStats, fetchHeatmap, fetchDaily, heatmapVar]);

  // Load hourly data when switching to timeseries tab
  useEffect(() => {
    if (activeTab === 'timeseries' && !hourlyData && datasetId) {
      fetchHourly();
    }
  }, [activeTab, hourlyData, datasetId, fetchHourly]);

  if (!datasetId) {
    return <div className="charts-empty">Select a dataset to view climate data</div>;
  }
  if (loading && !stats) {
    return <CenecLoadingBlock label="Loading climate data…" />;
  }

  return (
    <div className="climate-charts">
      {/* Tabs */}
      <div className="charts-tabs">
        {['overview', 'heatmap', 'timeseries'].map(tab => (
          <button
            key={tab}
            className={`charts-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' ? 'Overview' : tab === 'heatmap' ? 'Heatmap' : 'Hourly Explorer'}
          </button>
        ))}
      </div>

      {/* ================================================================
          OVERVIEW — adaptive: only show KPIs and charts for available variables
          ================================================================ */}
      {activeTab === 'overview' && stats && (() => {
        const av = stats.available_variables || [];
        const has = (v) => av.includes(v);
        const ds = stats; // alias
        const period = ds.data_start && ds.data_end
          ? `${ds.data_start.slice(0,10)} to ${ds.data_end.slice(0,10)}`
          : '';

        return (
          <div className="charts-panel">
            {/* Dataset summary one-liner */}
            <div className="dataset-summary-line">
              <span className="dataset-summary-vars">
                {av.map(v => VARIABLE_INFO[v]?.label || v).join(' · ')}
              </span>
              {period && <span className="dataset-summary-period">{period}</span>}
              <span className="dataset-summary-hours">{ds.total_hours?.toLocaleString()} hours</span>
            </div>

            {/* KPI cards — only for available variables */}
            <div className="kpi-grid">
              <div className="kpi-card">
                <span className="kpi-value">{ds.total_hours?.toLocaleString()}</span>
                <span className="kpi-label">Total Hours</span>
              </div>
              {has('ghi') && ds.avg_ghi != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.avg_ghi?.toFixed(0)}</span>
                  <span className="kpi-label">Avg GHI (W/m²)</span>
                </div>
              )}
              {has('ghi') && ds.total_ghi_kwh_m2 != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.total_ghi_kwh_m2?.toFixed(0)}</span>
                  <span className="kpi-label">Total GHI (kWh/m²)</span>
                </div>
              )}
              {has('temperature') && ds.avg_temp != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.avg_temp?.toFixed(1)}°C</span>
                  <span className="kpi-label">Avg Temperature</span>
                </div>
              )}
              {has('temperature') && ds.min_temp != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.min_temp?.toFixed(1)} / {ds.max_temp?.toFixed(1)}°C</span>
                  <span className="kpi-label">Min / Max Temp</span>
                </div>
              )}
              {has('wind_speed') && ds.avg_wind != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.avg_wind?.toFixed(1)} m/s</span>
                  <span className="kpi-label">Avg Wind Speed</span>
                </div>
              )}
              {has('humidity') && ds.avg_humidity != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.avg_humidity?.toFixed(0)}%</span>
                  <span className="kpi-label">Avg Humidity</span>
                </div>
              )}
              {has('pressure') && ds.avg_pressure != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.avg_pressure?.toFixed(0)}</span>
                  <span className="kpi-label">Avg Pressure (hPa)</span>
                </div>
              )}
              {has('precipitation') && ds.total_precip != null && (
                <div className="kpi-card">
                  <span className="kpi-value">{ds.total_precip?.toFixed(0)} mm</span>
                  <span className="kpi-label">Total Precipitation</span>
                </div>
              )}
            </div>

            {/* Charts — only for available variables */}
            {dailyData && dailyData.length > 0 && (
              <>
                {/* GHI + Temperature (if both available) or GHI only or Temp only */}
                {(has('ghi') || has('temperature')) && (() => {
                  const traces = [];
                  const layoutExtra = {};
                  if (has('ghi')) {
                    traces.push({ x: dailyData.map(d => d.time), y: dailyData.map(d => d.ghi), type: 'scatter', mode: 'lines', name: 'Daily GHI', line: { color: '#F59E0B', width: 1.5 }, fill: 'tozeroy', fillcolor: 'rgba(245,158,11,0.08)', yaxis: 'y' });
                    layoutExtra.yaxis = { ...darkLayout.yaxis, title: { text: 'GHI (W/m²)', font: { color: '#F59E0B', size: 12 } }, autorange: true };
                  }
                  if (has('temperature')) {
                    const axis = has('ghi') ? 'y2' : 'y';
                    traces.push({ x: dailyData.map(d => d.time), y: dailyData.map(d => d.temperature), type: 'scatter', mode: 'lines', name: 'Daily Temp', line: { color: '#EF4444', width: 1.5 }, yaxis: axis });
                    if (has('ghi')) {
                      layoutExtra.yaxis2 = { title: { text: 'Temperature (°C)', font: { color: '#EF4444', size: 12 } }, overlaying: 'y', side: 'right', gridcolor: 'rgba(0,0,0,0)', autorange: true, tickfont: { color: '#EF4444' } };
                    } else {
                      layoutExtra.yaxis = { ...darkLayout.yaxis, title: { text: 'Temperature (°C)', font: { color: '#EF4444', size: 12 } }, autorange: true };
                    }
                  }
                  const title = has('ghi') && has('temperature') ? 'Daily GHI & Temperature' : has('ghi') ? 'Daily GHI' : 'Daily Temperature';
                  const l = { ...darkLayout, title: { text: title, font: { size: 14 } }, xaxis: makeRangeXaxis(), ...layoutExtra, legend: { x: 0, y: 1.15, orientation: 'h', font: { size: 10 } }, height: 380, margin: { t: 60, r: 70, b: 40, l: 70 } };
                  return <ChartBox data={traces} layout={l} title={title} />;
                })()}

                {/* Wind Speed */}
                {has('wind_speed') && (() => {
                  const d = [{ x: dailyData.map(d => d.time), y: dailyData.map(d => d.wind_speed), type: 'scatter', mode: 'lines', name: 'Daily Wind Speed', line: { color: '#3B82F6', width: 1.5 }, fill: 'tozeroy', fillcolor: 'rgba(59,130,246,0.08)' }];
                  const l = { ...darkLayout, title: { text: 'Daily Average Wind Speed', font: { size: 14 } }, xaxis: makeRangeXaxis(), yaxis: { ...darkLayout.yaxis, title: { text: 'Wind Speed (m/s)', font: { color: '#3B82F6', size: 12 } }, autorange: true }, height: 320, margin: { t: 40, r: 30, b: 40, l: 70 } };
                  return <ChartBox data={d} layout={l} title="Daily Wind Speed" />;
                })()}

                {/* DNI + DHI */}
                {(has('dni') || has('dhi')) && (() => {
                  const traces = [];
                  if (has('dni')) traces.push({ x: dailyData.map(d => d.time), y: dailyData.map(d => d.dni), type: 'scatter', mode: 'lines', name: 'Daily DNI', line: { color: '#EF4444', width: 1.5 }, fill: 'tozeroy', fillcolor: 'rgba(239,68,68,0.06)' });
                  if (has('dhi')) traces.push({ x: dailyData.map(d => d.time), y: dailyData.map(d => d.dhi), type: 'scatter', mode: 'lines', name: 'Daily DHI', line: { color: '#F97316', width: 1.5 }, fill: 'tozeroy', fillcolor: 'rgba(249,115,22,0.06)' });
                  const l = { ...darkLayout, title: { text: 'Daily Average DNI & DHI', font: { size: 14 } }, xaxis: makeRangeXaxis(), yaxis: { ...darkLayout.yaxis, title: { text: 'Irradiance (W/m²)', font: { color: '#F97316', size: 12 } }, autorange: true }, legend: { x: 0, y: 1.12, orientation: 'h', font: { size: 10 } }, height: 340, margin: { t: 50, r: 30, b: 40, l: 70 } };
                  return <ChartBox data={traces} layout={l} title="Daily DNI & DHI" />;
                })()}

                {/* Humidity */}
                {has('humidity') && (() => {
                  const d = [{ x: dailyData.map(d => d.time), y: dailyData.map(d => d.humidity), type: 'scatter', mode: 'lines', name: 'Daily Humidity', line: { color: '#06B6D4', width: 1.5 }, fill: 'tozeroy', fillcolor: 'rgba(6,182,212,0.08)' }];
                  const l = { ...darkLayout, title: { text: 'Daily Average Humidity', font: { size: 14 } }, xaxis: makeRangeXaxis(), yaxis: { ...darkLayout.yaxis, title: { text: 'Humidity (%)', font: { color: '#06B6D4', size: 12 } }, autorange: true }, height: 280, margin: { t: 40, r: 30, b: 40, l: 70 } };
                  return <ChartBox data={d} layout={l} title="Daily Humidity" />;
                })()}

                {/* Pressure */}
                {has('pressure') && (() => {
                  const d = [{ x: dailyData.map(d => d.time), y: dailyData.map(d => d.pressure), type: 'scatter', mode: 'lines', name: 'Daily Pressure', line: { color: '#8B5CF6', width: 1.5 } }];
                  const l = { ...darkLayout, title: { text: 'Daily Average Pressure', font: { size: 14 } }, xaxis: makeRangeXaxis(), yaxis: { ...darkLayout.yaxis, title: { text: 'Pressure (hPa)', font: { color: '#8B5CF6', size: 12 } }, autorange: true }, height: 280, margin: { t: 40, r: 30, b: 40, l: 70 } };
                  return <ChartBox data={d} layout={l} title="Daily Pressure" />;
                })()}
              </>
            )}
          </div>
        );
      })()}

      {/* ================================================================
          HEATMAP — month x hour-of-day
          ================================================================ */}
      {activeTab === 'heatmap' && (
        <div className="charts-panel">
          <div className="heatmap-controls">
            <label>Variable:</label>
            <select
              value={heatmapVar}
              onChange={(e) => {
                setHeatmapVar(e.target.value);
                fetchHeatmap(e.target.value);
              }}
            >
              {Object.entries(VARIABLE_INFO)
                .filter(([k]) => !stats?.available_variables || stats.available_variables.includes(k))
                .map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
            </select>
          </div>

          {heatmapData && (() => {
            const d = [{ z: heatmapData.matrix, x: heatmapData.hours.map(h => `${h}:00`), y: MONTH_LABELS, type: 'heatmap', colorscale: VARIABLE_INFO[heatmapVar]?.colorscale || 'YlOrRd', colorbar: { title: { text: VARIABLE_INFO[heatmapVar]?.unit || '', font: { size: 10 } }, thickness: 15 }, hovertemplate: '%{y} %{x}<br>%{z:.1f} ' + (VARIABLE_INFO[heatmapVar]?.unit || '') + '<extra></extra>' }];
            const l = { ...darkLayout, title: { text: `${VARIABLE_INFO[heatmapVar]?.label} \u2014 Month vs Hour of Day`, font: { size: 14 } }, xaxis: { ...darkLayout.xaxis, title: 'Hour of Day', dtick: 2 }, yaxis: { ...darkLayout.yaxis, title: '', autorange: 'reversed' }, height: 400 };
            return <ChartBox data={d} layout={l} title={`${VARIABLE_INFO[heatmapVar]?.label} Heatmap`} />;
          })()}
        </div>
      )}

      {/* ================================================================
          HOURLY EXPLORER — full resolution with rangeslider + duration curve
          ================================================================ */}
      {activeTab === 'timeseries' && (
        <HourlyExplorer
          data={hourlyData}
          meta={hourlyMeta}
          availableVars={stats?.available_variables}
          durationCurves={durationCurves}
          fetchDurationCurve={fetchDurationCurve}
        />
      )}
    </div>
  );
};


/* =========================================================================
   Hourly Explorer — switchable variable, full-resolution with rangeslider
   ========================================================================= */
const HourlyExplorer = ({ data, meta, availableVars, durationCurves, fetchDurationCurve }) => {
  const filteredVars = Object.entries(VARIABLE_INFO).filter(([k]) => !availableVars || availableVars.includes(k));
  const [variable, setVariable] = useState(filteredVars[0]?.[0] || 'ghi');

  // Lazy-fetch duration curve when variable changes (server tdigest, milliseconds).
  useEffect(() => {
    if (variable && !durationCurves?.[variable] && fetchDurationCurve) {
      fetchDurationCurve(variable);
    }
  }, [variable, durationCurves, fetchDurationCurve]);

  if (!data || data.length === 0) {
    return <CenecLoadingBlock label="Loading climate data…" />;
  }

  const info = VARIABLE_INFO[variable] || { label: variable, unit: '', color: '#4ecca3' };
  const bucketLabel = meta?.bucket_hours
    ? (meta.bucket_hours === 1 ? 'hourly' :
       meta.bucket_hours < 24 ? `${meta.bucket_hours}h buckets` :
       meta.bucket_hours < 24 * 30 ? `${Math.round(meta.bucket_hours / 24)}d buckets` :
       `${Math.round(meta.bucket_hours / (24 * 30))}mo buckets`)
    : '';
  const curve = durationCurves?.[variable];

  return (
    <div className="charts-panel">
      <div className="heatmap-controls">
        <label>Variable:</label>
        <select value={variable} onChange={(e) => setVariable(e.target.value)}>
          {filteredVars.map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <span className="explorer-hint">
          Use the slider below the chart to zoom. Drag handles to resize. Drag middle to pan.
          {bucketLabel && <span style={{ marginLeft: 12, opacity: 0.7 }}>{'\u00b7 '}{bucketLabel}</span>}
        </span>
      </div>

      {/* Main time-series chart with rangeslider (server-downsampled) */}
      {(() => {
        const d = [{ x: data.map(d => d.time), y: data.map(d => d[variable]), type: 'scattergl', mode: 'lines', name: info.label, line: { color: info.color, width: 1 }, ...(info.positive !== false ? { fill: 'tozeroy', fillcolor: `${info.color}14` } : {}) }];
        const titleText = bucketLabel ? `${info.label} (${bucketLabel})` : info.label;
        const l = { ...darkLayout, title: { text: titleText, font: { size: 14 } }, xaxis: { ...makeRangeXaxis(), rangeslider: { ...makeRangeXaxis().rangeslider, thickness: 0.1 } }, yaxis: { ...darkLayout.yaxis, title: { text: info.unit, font: { color: info.color, size: 12 } }, autorange: true }, height: 450, margin: { t: 60, r: 30, b: 40, l: 70 } };
        return <ChartBox data={d} layout={l} config={{ ...plotConfig, displayModeBar: true, modeBarButtonsToRemove: ['lasso2d', 'select2d', 'toImage'] }} title={info.label} />;
      })()}

      {/* Duration curve - server-computed via timescaledb_toolkit tdigest */}
      {(() => {
        if (!curve) {
          return <div className="charts-loading" style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CenecLoadingRow label="Loading duration curve…" size="md" /></div>;
        }
        const d = [{ x: curve.data.map(p => p.x), y: curve.data.map(p => p.y), type: 'scattergl', mode: 'lines', name: 'Duration Curve', line: { color: info.color, width: 2 }, ...(info.positive !== false ? { fill: 'tozeroy', fillcolor: `${info.color}14` } : {}) }];
        const sourceNote = curve.source_rows ? `n = ${curve.source_rows.toLocaleString()} rows` : '';
        const titleText = sourceNote
          ? `${info.label} \u2014 Duration Curve  \u00b7  ${sourceNote}`
          : `${info.label} \u2014 Duration Curve`;
        const l = { ...darkLayout, title: { text: titleText, font: { size: 14 } }, xaxis: { ...darkLayout.xaxis, title: { text: '% of Time Exceeded', font: { size: 12 } }, range: [0, 100] }, yaxis: { ...darkLayout.yaxis, title: { text: info.unit, font: { color: info.color, size: 12 } }, autorange: true }, height: 300, margin: { t: 40, r: 30, b: 50, l: 70 } };
        return <ChartBox data={d} layout={l} title={`${info.label} Duration Curve`} />;
      })()}
    </div>
  );
};


export default ClimateCharts;
